import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { AsyncBoundary, Icon } from '@kollez/design-system';
import { useDrives } from '@/lib/queries';
import { formatLPA, formatIndianDate } from '@kollez/shared';

/**
 * /tpo/drives (F85a) — direct port of kollez/pages/tpo/drives.html.
 *   filterbar: .fb-search input · segmented (Running / Requests / Finished) · tier select · count
 *   .tbl with columns: Drive · Date · Eligible · Status · Applied · Offers
 */
type Seg = 'live' | 'upcoming' | 'done';

// Step 87 — DRIVE_STATUS is lowercase: {draft, open, assessing, interviewing, offers,
// closed, cancelled}. The old map compared to PascalCase/Underscored values that were
// NEVER in the enum (Announced, Applications_Open, Shortlisting, Testing, Interview,
// Offer_Extended, Requested, Archived). Result: `SEG_TO_STATUS[seg].some(s =>
// d.status?.startsWith(s))` was always false for every seg, so /tpo/drives showed
// ZERO drives on every tab regardless of DB state.
const SEG_TO_STATUS: Record<Seg, string[]> = {
  live: ['open', 'assessing', 'interviewing', 'offers'],
  upcoming: ['draft'],
  done: ['closed', 'cancelled'],
};

function statusBadge(status: string): { label: string; tone: string } {
  const s = status.replace(/_/g, ' ');
  if (/offer/i.test(s)) return { label: s, tone: 'badge-success' };
  if (/interview|short/i.test(s)) return { label: s, tone: 'badge-info' };
  if (/request|draft/i.test(s)) return { label: s, tone: 'badge-warning' };
  return { label: s, tone: '' };
}

export function TpoDrivesPage() {
  const nav = useNavigate();
  const { data, isLoading, error, refetch } = useDrives();
  const [seg, setSeg] = useState<Seg>('live');
  const [q, setQ] = useState('');
  const [tier, setTier] = useState<'' | '0' | '1' | '2'>('');

  const rows = data?.drives ?? [];
  const shown = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return rows.filter((d) => {
      const okSeg = SEG_TO_STATUS[seg].some((s) => d.status?.startsWith(s));
      const partnerName = (d.partner as { name?: string })?.name ?? '';
      // Drive hosts N roles now — search across every role's title.
      const roles = (d as { roles?: Array<{ opportunity?: { title?: string } }> }).roles ?? [];
      const roleTitles = roles.map((r) => r.opportunity?.title ?? '').join(' ');
      const okQ = !ql || `${partnerName} ${d.driveName} ${roleTitles}`.toLowerCase().includes(ql);
      const dTier = (d as { tier?: number }).tier;
      const okTier = tier === '' || String(dTier ?? '') === tier;
      return okSeg && okQ && okTier;
    });
  }, [rows, seg, q, tier]);

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Drives</h1>
          <p className="subhead">Every company visit this season, newest first.</p>
        </div>
        <div className="ph-actions">
          <Link to="/tpo/drives/new" className="btn btn-primary"><Icon name="file-plus" size={14} />Post a drive</Link>
        </div>
      </div>

      <div className="card">
        <div className="filterbar">
          <div className="input-group fb-search">
            <Icon name="search" size={14} />
            <input className="input" placeholder="Company or role" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {/* Deep-audit fix (Page 31/N): the middle segment was labeled
              'Requests' but SEG_TO_STATUS.upcoming maps only to 'draft'
              (TPO-composed drives not yet published). That's not a
              company request — it's a TPO draft. Relabeled 'Drafts' so
              the segment name matches the underlying status. */}
          <div className="segmented">
            <button aria-pressed={seg === 'live'} onClick={() => setSeg('live')}>Running</button>
            <button aria-pressed={seg === 'upcoming'} onClick={() => setSeg('upcoming')}>Drafts</button>
            <button aria-pressed={seg === 'done'} onClick={() => setSeg('done')}>Finished</button>
          </div>
          <select className="select" value={tier} onChange={(e) => setTier(e.target.value as typeof tier)}>
            <option value="">Any tier</option>
            <option value="0">Day 0</option>
            <option value="1">Day 1</option>
            <option value="2">Day 2</option>
          </select>
          <span className="fb-count">{shown.length} drive{shown.length === 1 ? '' : 's'}</span>
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={isLoading}
          error={error}
          empty={!isLoading && shown.length === 0}
          emptyTitle="No drives here yet"
          emptyHint="Post your first drive. It takes about 90 seconds if you paste the company's email."
          onRetry={() => void refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Drive</th>
                  <th className="hide-mobile">Date</th>
                  <th className="hide-mobile">Eligible</th>
                  <th>Status</th>
                  <th className="r">Applied</th>
                  <th className="r hide-mobile">Offers</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((d) => {
                  const partnerName = (d.partner as { name?: string })?.name ?? 'Company';
                  const roles = (d as { roles?: Array<{ opportunity?: { title?: string; compensation?: { ctc?: { min?: number } } } }> }).roles ?? [];
                  const firstOpp = roles[0]?.opportunity;
                  const oppTitle = firstOpp?.title ?? '';
                  const dTier = (d as { tier?: number }).tier ?? 1;
                  const ctc = firstOpp?.compensation?.ctc?.min ?? 0;
                  const roleCount = roles.length;
                  const eligible = (d as { eligibleCount?: number }).eligibleCount;
                  const applied = (d as { applicationsCount?: number }).applicationsCount;
                  const offers = (d as { offersCount?: number }).offersCount;
                  const badge = statusBadge(d.status);
                  const href = seg === 'upcoming' ? `/tpo/drives/new?fromRequest=${d._id}` : `/tpo/drives/${d._id}`;
                  return (
                    <tr
                      key={d._id}
                      tabIndex={0}
                      onClick={() => nav(href)}
                      onKeyDown={(e) => { if (e.key === 'Enter') nav(href); }}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div className="flex items-center gap-3">
                          <span className="logo-tile">{partnerName.slice(0, 2).toUpperCase()}</span>
                          <div className="min-w-0">
                            <div className="cell-title">
                              {partnerName}{oppTitle ? ` · ${oppTitle}` : ''}
                              {roleCount > 1 && (
                                <span className="badge badge-outline ml-2" style={{ verticalAlign: 'middle' }}>
                                  +{roleCount - 1} more role{roleCount - 1 === 1 ? '' : 's'}
                                </span>
                              )}
                            </div>
                            <div className="cell-sub">
                              <span className={`tier${dTier === 0 ? ' tier-0' : ''}`}>Day {dTier}</span>
                              {ctc ? ` · ${formatLPA(ctc)}` : ''}
                              {d.driveDate ? <span className="show-mobile inline"> · {formatIndianDate(d.driveDate)}</span> : null}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="hide-mobile">{d.driveDate ? formatIndianDate(d.driveDate) : '—'}</td>
                      <td className="hide-mobile num">{typeof eligible === 'number' ? eligible.toLocaleString('en-IN') : '—'}</td>
                      <td><span className={`badge ${badge.tone}`}>{badge.label}</span></td>
                      <td className="r">{typeof applied === 'number' ? applied.toLocaleString('en-IN') : '—'}</td>
                      <td className="r hide-mobile">{typeof offers === 'number' ? offers : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>
    </div>
  );
}
